import React, { useState, useEffect } from 'react';
import { Typography, Box, CircularProgress, Paper, Grid, Chip, LinearProgress, Divider, useTheme } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import BiotechIcon from '@mui/icons-material/Biotech';
import PieChartIcon from '@mui/icons-material/PieChart';
import MapIcon from '@mui/icons-material/Map';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

export default function EpidemiologyAnalytics() {
  const theme = useTheme();
  const [progress, setProgress] = useState(0);
  const [activeModule, setActiveModule] = useState(0);
  const [dataPointsLoaded, setDataPointsLoaded] = useState(0);

  // Analytics modules in development
  const modules = [
    {
      name: "Disease Progression Modeling",
      description: "AI-powered analysis of skin condition development patterns over time",
      icon: <TimelineIcon fontSize="large" sx={{ color: theme.palette.success.main }} />,
      completion: 35
    },
    {
      name: "Geographical Distribution Maps",
      description: "Interactive heat maps showing prevalence of dermatological conditions by region",
      icon: <MapIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      completion: 28
    },
    {
      name: "Treatment Efficacy Analytics",
      description: "Comparative analytics of treatment outcomes across patient demographics",
      icon: <PieChartIcon fontSize="large" sx={{ color: theme.palette.secondary.main }} />,
      completion: 42
    },
    {
      name: "Research Correlation Engine",
      description: "Connect clinical observations with published medical research",
      icon: <BiotechIcon fontSize="large" sx={{ color: theme.palette.error.main }} />,
      completion: 18
    },
    {
      name: "Predictive Outbreak Detection",
      description: "Early warning system for detecting emerging skin condition patterns",
      icon: <TrendingUpIcon fontSize="large" sx={{ color: theme.palette.warning.main }} />,
      completion: 15
    }
  ];

  // Simulate development progress
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => 
        prevProgress >= 100 ? 0 : prevProgress + 1
      );
    }, 800);
    
    const moduleTimer = setInterval(() => {
      setActiveModule((prevModule) => 
        prevModule >= modules.length - 1 ? 0 : prevModule + 1
      );
    }, 4000);
    
    const dataTimer = setInterval(() => {
      setDataPointsLoaded((prev) => prev < 10000000 ? prev + 123456 : 0);
    }, 300);
    
    return () => {
      clearInterval(timer);
      clearInterval(moduleTimer);
      clearInterval(dataTimer);
    };
  }, []);

  return(
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="80vh"
      sx={{
        background: theme.palette.mode === 'light' 
          ? 'linear-gradient(135deg, #e8f5e9 0%, #e1f5fe 100%)'
          : 'linear-gradient(135deg, #1a2027 0%, #0d2138 100%)',
        padding: 4
      }}
    >
      <Paper 
        elevation={4} 
        sx={{ 
          p: 5, 
          borderRadius: 4, 
          maxWidth: 900,
          background: theme.palette.mode === 'light'
            ? 'rgba(255, 255, 255, 0.9)'
            : 'rgba(26, 32, 39, 0.8)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${theme.palette.mode === 'light' 
            ? 'rgba(209, 213, 219, 0.3)'
            : 'rgba(76, 86, 106, 0.3)'}`
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} textAlign="center">
            <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom>
              Epidemiological Analytics
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Transforming clinical data into actionable insights
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mt: 1, mb: 3 }}>
              <Chip label="Disease Evolution" color="primary" />
              <Chip label="Trend Analysis" color="secondary" />
              <Chip label="Predictive Modeling" color="success" />
              <Chip label="Population Health" color="info" />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6} textAlign="center">
            <Box position="relative" display="inline-flex" sx={{ mb: 3 }}>
              <CircularProgress 
                variant="determinate" 
                value={progress} 
                size={200}
                thickness={3}
                sx={{ color: theme.palette.success.main }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {modules[activeModule].icon}
                <Typography variant="caption" component="div" sx={{ fontSize: '1.1rem', mt: 1 }}>
                  {`${Math.round(progress)}%`}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ px: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Data points processed
              </Typography>
              <Typography variant="h5" sx={{ fontFamily: 'monospace', my: 1 }}>
                {dataPointsLoaded.toLocaleString()}
              </Typography>
              <LinearProgress variant="indeterminate" sx={{ height: 6, borderRadius: 3 }} />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h5" fontWeight="medium" gutterBottom>
              {modules[activeModule].name}
            </Typography>
            <Typography variant="body1" paragraph>
              {modules[activeModule].description}
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Module development progress:
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={modules[activeModule].completion} 
                sx={{ height: 8, borderRadius: 2, mb: 1 }} 
              />
              <Typography variant="caption" color="text.secondary">
                {modules[activeModule].completion}% complete
              </Typography>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="body2">
              Our team is developing advanced epidemiological analytics tools to help medical professionals identify trends, track disease evolution, and make data-driven decisions for patient care and public health initiatives.
            </Typography>
            
            <Typography variant="subtitle2" sx={{ mt: 2, color: 'text.secondary' }}>
              Anticipated beta release: Q3 2026
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                Modules in development:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {modules.map((module, index) => (
                  <Chip 
                    key={index}
                    label={module.name}
                    size="small"
                    variant={index === activeModule ? "filled" : "outlined"}
                    color={index === activeModule ? "primary" : "default"}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
